import fetcher from '@app/utils/fetcher';
import Loader from '@store/components/Loader';
import type { FC} from 'react';
import React, { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import type { ISearchResult, ISearchResultItem } from './types';

interface IProps {
  apiUrl: string;
  searchTerm: string;
  searchLink: string;
}

const ItemsGroup: FC<{ items: ISearchResultItem[]; title: string }> = ({ items, title }) => {
  if (!items || items.length === 0) return <></>;

  return (
    <div className="py-2 border-t-0 border-b border-gray-300 last:border-b-0">
      <span className="block pb-1 px-4 text-gray-500 text-sm font-medium font-heading text-left">
        {title}
      </span>
      <div className="grid gap-y-1">
        {items.map(item => (
          <SearchListItem key={item.id} {...item} />
        ))}
      </div>
    </div>
  );
};

const SearchListItem: FC<ISearchResultItem> = props => {
  const [hasImage, setValidPicture] = useState(!!props.imageUrl);

  return (
    <a
      href={props.url}
      key={props.id}
      title={`${props.code} - ${props.name}`}
      className="flex gap-2 text-sm h-10 px-4 hover:text-primary">
      <div className="max-w-[2.5rem] w-full inline-flex justify-center items-center flex-shrink-0 overflow-hidden">
        {hasImage && props.imageUrl && (
          <img
            className="max-h-full object-scale-down"
            src={props.imageUrl}
            alt={props.name}
            onError={() => setValidPicture(false)}
          />
        )}
      </div>
      <span className="inline-flex items-center overflow-hidden">
        <span className="w-[30ch] inline-block font-medium truncate">{props.name}</span>
      </span>
    </a>
  );
};

export default function SearchResults({ apiUrl, searchTerm, searchLink }: IProps) {
  const { t } = useTranslation();

  const ref = createRef<HTMLDivElement>();
  const [isFocused, setFocused] = useState(true);

  const { data, error } = useSWR({ apiUrl, searchTerm }, ({ apiUrl, searchTerm }) =>
    fetcher<ISearchResult>(apiUrl, { searchParams: { searchTerm } })
  );

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (ref.current?.contains(event.target as Node)) {
        event.stopPropagation();
        return;
      }

      if (isFocused) {
        setFocused(false);
      }
    };

    document.addEventListener('click', handler, false);
    return () => document.removeEventListener('click', handler);
  });

  if (error) {
    console.error(error);
    return <></>;
  }

  return (
    <div
      ref={ref}
      className={`w-full absolute top-10 z-50 border border-gray-300 rounded-none bg-white text-black -mt-px overflow-hidden ${
        isFocused ? 'block' : 'hidden'
      }`}>
      {!data && (
        <div className="h-12 flex justify-center items-center">
          <Loader className="text-base text-primary" />
        </div>
      )}
      {data && (
        <>
          <div className="grid">
            <ItemsGroup items={data.products} title={t('Products')} />
            <ItemsGroup items={data.categories} title={t('Categories')} />
            {data.products.length === 0 && data.categories.length === 0 && (
              <div className="py-2 px-4 text-center">
                <span className="text-gray-500 text-sm">Neboli nájdené žiadne výsledky</span>
              </div>
            )}
          </div>
          {(data.products.length > 0 || data.categories.length > 0) && (
            <div>
              <a
                href={searchLink}
                className="block text-center py-1 text-sm bg-gray-100 text-gray-700 mt-2 hover:bg-gray-500 hover:text-gray-100">
                {t('ToShowAllResults')}
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
}
